import { useState, useEffect } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Wallet, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';

interface Driver {
  id: string;
  name: string;
  phone?: string;
}

interface GeneratePaySettlementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

function firstOfMonthIso(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function GeneratePaySettlementModal({
  open,
  onOpenChange,
  onSuccess,
}: GeneratePaySettlementModalProps) {
  const { t } = useI18n();
  const { get } = useApi();
  const { post, loading } = useApi({ showSuccessToast: false, showErrorToast: false });
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [periodStart, setPeriodStart] = useState<string>(firstOfMonthIso());
  const [periodEnd, setPeriodEnd] = useState<string>(todayIso());
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  useEffect(() => {
    if (open) {
      fetchDrivers();
      setSelectedDriverId('');
      setPeriodStart(firstOfMonthIso());
      setPeriodEnd(todayIso());
      setNotes('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchDrivers = async () => {
    setLoadingDrivers(true);
    try {
      const result: any = await get('/api/v1/dashboard/drivers');
      const data = result?.success !== undefined ? result?.data : result;
      const list = data?.data ?? data?.list ?? data?.drivers ?? (Array.isArray(data) ? data : []);
      setDrivers(
        list.map((d: any) => ({
          id: d.id,
          name: d.user?.name ?? d.name ?? d.id,
          phone: d.user?.phone ?? d.phone,
        }))
      );
    } catch {
      toast.error('فشل في تحميل قائمة السائقين');
    } finally {
      setLoadingDrivers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedDriverId) {
      setError('يرجى اختيار سائق');
      return;
    }
    if (!periodStart || !periodEnd) {
      setError('يرجى تحديد فترة التسوية');
      return;
    }

    try {
      await post(`/api/v1/driver-pay-settlements/${selectedDriverId}/generate`, {
        period_start: periodStart,
        period_end: periodEnd,
        notes: notes.trim() || undefined,
      });
      toast.success(t.financials.settlement_generated);
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      const msg = err?.message ?? 'فشل في إنشاء التسوية';
      setError(msg);
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]" dir="rtl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
              <Wallet className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <DialogTitle>{t.financials.generate_pay_settlement}</DialogTitle>
              <DialogDescription className="text-xs">
                إنشاء تسوية أجر للسائق عن فترة محددة
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="pay-driver">
              السائق <span className="text-destructive">*</span>
            </Label>
            {loadingDrivers ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                جاري تحميل السائقين...
              </div>
            ) : (
              <Select
                value={selectedDriverId}
                onValueChange={setSelectedDriverId}
                disabled={loading}
              >
                <SelectTrigger id="pay-driver">
                  <SelectValue placeholder="اختر سائقاً..." />
                </SelectTrigger>
                <SelectContent>
                  {drivers.length === 0 ? (
                    <SelectItem value="_empty" disabled>
                      لا يوجد سائقون
                    </SelectItem>
                  ) : (
                    drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        <span>{d.name}</span>
                        {d.phone && (
                          <span className="text-muted-foreground text-xs me-2">{d.phone}</span>
                        )}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="period-start">
                {t.financials.period_start} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="period-start"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="period-end">
                {t.financials.period_end} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="period-end"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pay-notes">ملاحظات (اختياري)</Label>
            <Textarea
              id="pay-notes"
              placeholder="أضف ملاحظة توضيحية..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              rows={3}
              className="resize-none"
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              إلغاء
            </Button>
            <Button type="submit" disabled={loading || !selectedDriverId} className="gap-2">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  جاري الإنشاء...
                </>
              ) : (
                <>
                  <Wallet className="h-4 w-4" />
                  {t.financials.generate_pay_settlement}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
